import { motion } from 'framer-motion';
import { createContext, useContext, type ComponentProps } from 'react';

import Button from '@poseidon/components/Input2/Button';
import { cn } from '@poseidon/components/cn';
import { General } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { useElementSize } from '@/hooks/useElementSize';
import { useRouterQuery } from '@/hooks/useRouterQuery';

/**
 * Width (px) of the content area the bar sits in, for `SelectionBarActions` to collapse against.
 * `null` until measured, and outside a container (the bar then shows every label).
 */
const SelectionBarWidthContext = createContext<number | null>(null);
export const useSelectionBarAvailableWidth = () => useContext(SelectionBarWidthContext);

type SelectPopupContainerProps = {
  idsKey?: 'selectedIds' | 'orgIds' | 'selectedCandidateIds' | 'selectedSwitchCandidateIds' | 'oraclePersonIds';
  loading: string | undefined;
  children: ComponentProps<'div'>['children'];
};
export default function SelectPopupContainer({ idsKey, loading, children }: SelectPopupContainerProps) {
  const [query, , updateQuery] = useRouterQuery();
  const [containerRef, containerSize] = useElementSize<HTMLDivElement>();

  const onClose = () => updateQuery({ [idsKey || 'selectedIds']: undefined });

  return (
    // Full-width, zero-height box pinned to the bottom of the layout. Its width tracks the
    // surrounding content area — not the bar's own content, which would be circular — so it is
    // what the bar's actions measure to decide what fits (see SelectionBarActions). It has no
    // height and `pointer-events-none` so the invisible full-width box never swallows clicks;
    // the bar re-enables pointer events on itself.
    <div ref={containerRef} className="@container pointer-events-none absolute inset-x-0 bottom-0 z-50">
      <SelectionBarWidthContext.Provider value={containerSize?.width ?? null}>
        <motion.div
          key="div"
          initial={{ x: '-50%', y: '100%' }}
          exit={{ x: '-50%', y: 'calc(100% + 20px, 0))' }}
          animate={{ x: '-50%', y: 0 }}
          transition={{ type: 'spring', stiffness: 250, mass: 0.8 }}
          className={cn(
            // Centering is owned by framer-motion's `x: '-50%'` below. Do NOT add a
            // Tailwind `-translate-x-1/2` here: in Tailwind v4 translate utilities use
            // the standalone `translate` property, which COMPOSES with framer's
            // `transform` instead of being overridden by it — yielding a double -50%
            // shift that pushes the bar off the left edge.
            'pointer-events-auto absolute bottom-2.5 left-1/2 flex flex-row items-center gap-3 text-nowrap rounded-lg border px-4 py-3 shadow-md',
            'border-lynch-300 bg-lynch-900 text-white',
          )}>
          {!loading && (
            <>
              <Button
                icon={<General.XMark />}
                onClick={onClose}
                text
                aria-label="Clear selection"
                className="text-white hover:text-neutral-600"
              />
              <span>{query[idsKey ?? 'selectedIds']?.length} selected</span>
              {children}
            </>
          )}
          {loading && (
            <>
              <WaveSpinner className="size-4" />
              {loading}
            </>
          )}
        </motion.div>
      </SelectionBarWidthContext.Provider>
    </div>
  );
}
